import { useRef, useState } from 'react';
import { Dialog, DialogProps } from '../ui';
import { ExitIcon } from '../icons';
import Toast from '../../components/common/pop-up-message';
import { Dependent, useInviteDependents } from '../../hooks/api/useInviteDependents';
import useAuth from '../../hooks/utils/useAuth';

export interface RemoveDependentModalProps extends DialogProps {
  dependent: Dependent | undefined;
}

export const RemoveDependentModal = ({ dependent, onClose, ...props }: RemoveDependentModalProps) => {
  const toastNotificationContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { removeDependent } = useInviteDependents(user?.id);
  const [inProgress, setInProgress] = useState<boolean>(false);

  const removeInviteDependent = (dependentId: number) => {
    setInProgress(true);

    return new Promise((res) =>
      removeDependent.mutate(dependentId, {
        onSuccess: () => {
          Toast.success(
            `You removed ${dependent?.first_name} ${dependent?.last_name}`,
            undefined,
            toastNotificationContainerRef?.current,
          );

          res(null);
          onClose();
        },
        onError: (error) => {
          // @ts-ignore
          const errorMessage = error?.response?.data?.description;

          if (errorMessage) {
            Toast.error(
              `There was an error removing your dependent. ${
                errorMessage ? errorMessage : 'Please contact your administrator.'
              }`,
              undefined,
              toastNotificationContainerRef?.current,
            );
          }

          res(error);
        },
        onSettled: () => setInProgress(false),
      }),
    );
  };

  return (
    <>
      <div ref={toastNotificationContainerRef} />
      <Dialog
        {...props}
        onClose={onClose}
        closeOnOutsideClick={true}
        className="shadow-modal backdrop:bg-modalBackdropColor w-full max-w-md rounded-xl bg-white p-0"
      >
        <div className="border-input-border-gray flex items-start justify-between border-b p-6 md:p-8">
          <div className="flex flex-col gap-2 pr-4">
            <h2 className="font-albert-sans text-2xl font-semibold leading-[30px] tracking-[-0.2px] text-black">
              Are you sure?
            </h2>
            <p className="font-['Open_Sans'] text-sm font-normal leading-6 text-gray-700">
              Are you sure you want to remove {dependent?.first_name} {dependent?.last_name}? They may not be able to
              access your benefit any more.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
            aria-label="Close modal"
          >
            <ExitIcon color="#22282F" />
          </button>
        </div>
        <div className="flex flex-col gap-3 p-6 md:p-8">
          <button
            type="button"
            onClick={() => removeInviteDependent(dependent?.id ?? -1)}
            disabled={inProgress}
            className="bg-button-primary hover:bg-button-primary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 font-['Open_Sans'] text-base font-medium leading-5 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {inProgress ? (
              <span className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Removing...
              </span>
            ) : (
              'Remove'
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={inProgress}
            className="bg-button-secondary text-button-primary hover:bg-button-secondary-hover inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5 font-['Open_Sans'] text-base font-medium leading-5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </Dialog>
    </>
  );
};
